import { Fragment } from "react";
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
  createColumnHelper,
} from "@tanstack/react-table";
import { ArrowDownIcon, ArrowUpIcon } from "@heroicons/react/24/outline";

import { IDailyCheckStep } from "../../models/DailyChecks";

const colHelper = createColumnHelper<IDailyCheckStep>();

const cols = [
  colHelper.display({
    id: "name",
    header: "Name",
    sortingFn: "auto",
    cell: (info) => (
      <div className="ml-4">
        <div>{info.row.original.name}</div>
      </div>
    )
  }),
  colHelper.display({
    id: "order",
    header: "Order",
    sortingFn: "auto",
    cell: (info) => (
      <span>{info.row.original.order}</span>
    )
  }),
  colHelper.display({
    id: "isActive",
    header: "Active",
    sortingFn: "auto",
    cell: (info) => (
      <span className={info.row.original.isActive ? "text-green-700" : "text-gray-500"}>
        {info.row.original.isActive ? "Active" : "Inactive"}
      </span>
    )
  }),
];

export default function DailyCheckStepsTable({
  steps,
  setStep,
  moveStep,
  isReordering,
}: {
  steps: IDailyCheckStep[],
  setStep: (step: IDailyCheckStep) => void,
  moveStep: (stepId: string, direction: "up" | "down") => void,
  isReordering: boolean,
}) {
  const table = useReactTable({
    data: steps,
    columns: cols,
    enableRowSelection: true,
    getExpandedRowModel: getExpandedRowModel(),
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <>
      <div className="mt-4 bg-white overflow-auto">
        <table className="min-w-full overflow-scroll divide-y border divide-gray-300">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="bg-primary-50 text-gray-800">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    scope="col"
                    className="py-3.5 px-3 text-sm font-semibold lg:table-cell text-left"
                  >
                    {header.isPlaceholder
                      ? null
                      : (
                        <div>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </div>
                      )}
                  </th>
                ))}
                <th
                  scope="col"
                  className="py-3.5 px-3 text-sm font-semibold lg:table-cell text-left"
                >
                  Move
                </th>
              </tr>
            ))}
          </thead>

          <tbody>
            {table.getRowModel().rows.map((row, i) => {
              let rowCells = row.getVisibleCells();
              return (
                <Fragment key={i}>
                  <tr
                    key={row.id}
                    onClick={() => setStep(row.original)}
                    className="hover:bg-gray-100 hover:cursor-pointer"
                  >
                    {rowCells.map((cell) => (
                      <td key={cell.id} className="px-3 py-3 border text-sm">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                    <td className="px-3 py-3 border text-sm">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="inline-flex h-7 w-7 items-center justify-center rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            moveStep(row.original.id, "up");
                          }}
                          disabled={isReordering || row.index === 0}
                        >
                          <ArrowUpIcon className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          className="inline-flex h-7 w-7 items-center justify-center rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            moveStep(row.original.id, "down");
                          }}
                          disabled={isReordering || row.index === steps.length - 1}
                        >
                          <ArrowDownIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {row.getIsExpanded() && (
                    <></>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
